<template>
  <div
    @click.once="handleClick"
    :class="{
      'plr-video-hint-widget': true,
      'backdrop-dark-50': true,
      'size-sm': videoHint?.size === 'small',
      'no-controls': !videoHint?.showControls
    }"
  >
    <div
      @click.self="() => (playing ? handlePauseClick() : handlePlayClick())"
      class="video-container"
    >
      <h4 v-if="videoHint?.title" :dir="getTextDirection(videoHint.title)">
        {{ videoHint.title }}
      </h4>
      <video
        ref="videoRef"
        :class="width && height ? { portrait: width < height } : {}"
        :style="width && height ? { aspectRatio: width / height } : {}"
        :loop="!!videoHint?.looped"
        :muted="muted"
        autoplay
        playsinline
      />
      <span v-if="isMuteForced" class="plr-btn-unmute" />
      <input
        v-if="videoHint?.showControls"
        v-model="currentTime"
        type="range"
        min="0"
        :max="duration"
        class="flex-grow-1 flex-shrink-1"
      />
      <div v-if="videoHint?.showControls" class="video-controls">
        <button
          @click="currentTime = max([0, currentTime - 15])"
          :disabled="!currentTime"
          class="btn-back-15sec ms-10"
        />
        <button
          @click="() => (playing ? handlePauseClick() : handlePlayClick())"
          :class="['btn-playback', playing ? 'pause' : 'play']"
        />
        <button
          @click="currentTime = min([duration, currentTime + 15])"
          :disabled="currentTime >= duration"
          class="btn-fwd-15sec"
        />
        <button
          @click="muted = !muted"
          :class="['btn-vol', 'ms-auto', muted ? 'muted' : 'unmuted']"
        />
        <input
          v-model="volume"
          type="range"
          min="0"
          max="1"
          step="0.0000000000000001"
          class="plr-volume-slider"
        />
      </div>
      <div class="hint-toolbar">
        <button @click="handleCloseClick" class="btn-close" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  MessageType,
  RequestedAction,
  VideoHintDto
} from "@moderrotech/servers-communication-library/src/types";
import { useMediaControls } from "@vueuse/core";
import { getTextDirection } from "@/helpers/string";
import _ from "lodash";
import { OSName } from "ua-parser-js/src/enums/ua-parser-enums";
import { getUaParserResult } from "@moderrotech/servers-communication-library/src/helpers/device";

export default {
  setup() {
    const store = useStore();

    const videoHint = computed(
      (): VideoHintDto => store.state.session.videoHint
    );

    const isMuteForced = ref(
      getUaParserResult()?.os?.name === OSName.IOS &&
        !store.state.config.config?.mobileAppData &&
        !videoHint.value?.muted
    );

    const width = ref<number>(),
      height = ref<number>();

    const videoRef = ref<HTMLVideoElement>();

    watch(
      () => videoRef.value,
      v => {
        if (v) {
          v.onloadedmetadata = () => {
            width.value = v.videoWidth;
            height.value = v.videoHeight;
          };
        }
      },
      { immediate: true }
    );

    const {
      ended,
      playing,
      muted,
      volume,
      duration,
      currentTime
    } = useMediaControls(videoRef, {
      src: videoHint.value!.url
    });

    muted.value = !!videoHint.value?.muted || isMuteForced.value;
    playing.value = true;

    const sendEvent = (name: "ended" | "play-clicked" | "pause-clicked") =>
      store.dispatch("session/sendJsonMessage", {
        message: {
          type: MessageType.videoHintEvent,
          name
        }
      });

    watch(
      () => ended.value,
      v => {
        if (v) {
          if (videoHint.value?.looped) {
            return;
          }

          sendEvent("ended");
          store.commit("session/UNSET_VIDEO_HINT");
        }
      },
      { immediate: true }
    );

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    watch(
      () =>
        _.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.controlVideoHint
        ),
      v => {
        if (v) {
          const payload = v.payload as
            | {
                command: "seek" | "seek-relative";
                time: number;
              }
            | {
                command: "set-volume";
                volume: number;
              }
            | {
                command: "mute" | "unmute" | "play" | "pause";
              };

          if (payload.command === "seek") {
            currentTime.value = _.min([duration.value, payload.time])!;
          } else if (payload.command === "seek-relative") {
            currentTime.value =
              payload.time > 0
                ? _.min([duration.value, currentTime.value + payload.time])!
                : _.max([0, currentTime.value + payload.time])!;
          } else if (payload.command === "set-volume") {
            volume.value = payload.volume;
          } else if (payload.command === "mute") {
            muted.value = true;
          } else if (payload.command === "unmute") {
            muted.value = false;
          } else if (payload.command === "play") {
            playing.value = true;
          } else if (payload.command === "pause") {
            playing.value = false;
          }

          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.controlVideoHint
          );
        }
      },
      { immediate: true }
    );

    const handleClick = () => {
      if (isMuteForced.value) {
        isMuteForced.value = false;
        muted.value = false;
      }
    };

    return {
      getTextDirection,
      videoHint,
      videoRef,
      width,
      height,
      playing,
      muted,
      volume,
      duration,
      currentTime,
      isMuteForced,
      handleClick,
      handlePlayClick: () => {
        if (!(videoHint.value?.showControls ?? true)) {
          return;
        }

        sendEvent("play-clicked");
        playing.value = !playing.value;
      },
      handlePauseClick: () => {
        if (!(videoHint.value?.showControls ?? true)) {
          return;
        }

        sendEvent("pause-clicked");
        playing.value = !playing.value;
      },
      handleCloseClick: () => store.commit("session/UNSET_VIDEO_HINT"),
      ..._.pick(_, ["max", "min"])
    };
  }
};
</script>

<style lang="scss">
@import "../assets/styles/video-hint.scss";
@import "../assets/styles/kiosk-landscape-30inch/video-hint.scss";
@import "../assets/styles/kiosk-portrait-30inch/video-hint.scss";
</style>
